import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FloatField, FloatWrapper } from "@/components/ui/FormField";
import { EmployeeCombobox } from "@/components/ui/EmployeeCombobox";
import { Upload, FileText, Loader2, GitBranch } from "lucide-react";
import { DatePicker } from "@/components/ui/DatePicker";
import {
  apiCreateHRDocument,
  apiUploadHRDocumentFile,
  apiGetDocumentChainTemplates,
  apiSaveDocumentChainTemplate,
  apiStartDocumentChain,
  getDocumentUploadErrorI18nKey,
  validateDocumentUploadFile,
  DOCUMENT_TYPES,
  EXPIRY_DOCUMENT_TYPES,
} from "@/lib/api";
import type { HRDocument, DocumentType, DocumentChainStep, DocumentChainTemplate } from "@/lib/api";
import { toast } from "sonner";
import { DocumentChainBuilder } from "./DocumentChainBuilder";

interface EmployeeOption {
  id: number;
  name: string;
  jobTitle?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDocumentCreated: (doc: HRDocument) => void;
  employees?: EmployeeOption[];
  defaultEmployeeId?: number;
}

export function UploadDocumentModal({
  open,
  onOpenChange,
  onDocumentCreated,
  employees = [],
  defaultEmployeeId,
}: Props) {
  const { t } = useTranslation();
  const requiresEmployeeSelection = employees.length > 0;
  const initialEmployeeId = defaultEmployeeId ? String(defaultEmployeeId) : "";
  const [employeeId, setEmployeeId] = useState(initialEmployeeId);
  const [documentName, setDocumentName] = useState("");
  const [documentType, setDocumentType] = useState<DocumentType | "">("");
  const [documentExpiry, setDocumentExpiry] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const submissionRef = useRef(false);

  // Chain state
  const [steps, setSteps] = useState<DocumentChainStep[]>([]);
  const [templates, setTemplates] = useState<DocumentChainTemplate[]>([]);

  useEffect(() => {
    if (open) {
      setEmployeeId(defaultEmployeeId ? String(defaultEmployeeId) : "");
      apiGetDocumentChainTemplates()
        .then(setTemplates)
        .catch(() => {});
    }
  }, [defaultEmployeeId, open]);

  const needsExpiry =
    documentType !== "" &&
    EXPIRY_DOCUMENT_TYPES.includes(documentType as DocumentType);

  const isValid =
    (!requiresEmployeeSelection || Boolean(employeeId)) &&
    Boolean(documentName.trim()) &&
    Boolean(documentType) &&
    Boolean(file) &&
    (!needsExpiry || Boolean(documentExpiry));

  const reset = () => {
    setEmployeeId(defaultEmployeeId ? String(defaultEmployeeId) : "");
    setDocumentName("");
    setDocumentType("");
    setDocumentExpiry("");
    setFile(null);
    setSteps([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleOpenChange = (val: boolean) => {
    if (!loading) {
      if (!val) reset();
      onOpenChange(val);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] ?? null;
    if (selectedFile) {
      const validationCode = validateDocumentUploadFile(selectedFile);
      if (validationCode) {
        const translationKey = getDocumentUploadErrorI18nKey({ code: validationCode });
        toast.error(t("documents.messages.uploadFailed"), {
          description: translationKey ? t(translationKey) : undefined,
        });
        setFile(null);
        e.target.value = "";
        return;
      }
    }
    setFile(selectedFile);
    if (selectedFile && !documentName.trim()) {
      setDocumentName(
        selectedFile.name.replace(/\.pdf$/i, "").replace(/[-_]/g, " "),
      );
    }
  };

  const handleSaveTemplate = async (name: string, chainSteps: DocumentChainStep[]) => {
    try {
      const saved = await apiSaveDocumentChainTemplate(name, chainSteps);
      setTemplates((prev) => [saved, ...prev]);
      toast.success(t("documents.chain.templateSaved", { name }));
    } catch (err: any) {
      toast.error(t("documents.chain.templateSaveFailed"), { description: err.message });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || !file || submissionRef.current) return;

    submissionRef.current = true;
    setLoading(true);
    try {
      const created = await apiCreateHRDocument({
        employeeId:
          requiresEmployeeSelection && employeeId
            ? Number(employeeId)
            : undefined,
        documentName: documentName.trim(),
        documentType: documentType as DocumentType,
        documentExpiry: documentExpiry || undefined,
      });

      const withFile = await apiUploadHRDocumentFile(created.id, file);

      if (steps.length > 0) {
        try {
          await apiStartDocumentChain(withFile.id, steps);
        } catch {
          toast.warning(t("documents.chain.chainStartFailed"));
        }
      }

      onDocumentCreated(withFile);
      toast.success(t("documents.messages.uploadSubmitted"), {
        description: t("documents.messages.uploadSubmittedDesc", {
          name: withFile.documentName,
        }),
      });
      handleOpenChange(false);
    } catch (err: any) {
      const translationKey = getDocumentUploadErrorI18nKey(err);
      toast.error(t("documents.messages.uploadFailed"), {
        description: translationKey ? t(translationKey) : err.message,
      });
    } finally {
      submissionRef.current = false;
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[900px] p-0 gap-0 overflow-hidden">
        <div className="px-6 pt-6 pb-4 bg-muted/30 border-b">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2.5 text-base font-semibold">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Upload className="h-4 w-4 text-primary" />
              </div>
              {t("documents.uploadModal.title")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mt-1.5">
            {t("documents.uploadModal.description")}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="flex flex-row min-h-0" style={{ maxHeight: "65vh" }}>
            {/* Left panel — document fields */}
            <div className="flex-1 px-6 py-4 border-r overflow-y-auto">
              {requiresEmployeeSelection && (
                <FloatWrapper label={t("documents.employeeLabel")} required>
                  <EmployeeCombobox
                    employees={employees}
                    value={employeeId}
                    onChange={setEmployeeId}
                    placeholder={t("documents.selectEmployee")}
                    disabled={loading}
                  />
                </FloatWrapper>
              )}

              <FloatWrapper label={t("documents.documentTypeLabel")} required>
                <Select
                  value={documentType}
                  onValueChange={(value) => {
                    setDocumentType(value as DocumentType);
                    setDocumentExpiry("");
                  }}
                  disabled={loading}
                >
                  <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4">
                    <SelectValue placeholder={t("documents.selectType")} />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {t(`documents.type.${type.replace(/\s+/g, "")}`, { defaultValue: type })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FloatWrapper>

              <FloatField
                label={t("documents.documentName")}
                required
                placeholder={t("documents.uploadModal.namePlaceholder")}
                value={documentName}
                onChange={(v) => setDocumentName(v as string)}
              />

              {needsExpiry && (
                <>
                  <FloatWrapper label={t("documents.uploadModal.expiryLabel")} required>
                    <DatePicker
                      value={documentExpiry}
                      onChange={setDocumentExpiry}
                      disabled={loading}
                      disablePastDates={true}
                      className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm"
                    />
                  </FloatWrapper>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("documents.uploadModal.expiryHint")}
                  </p>
                </>
              )}

              <div className="mt-3 space-y-1.5">
                <p className="text-[13px] font-bold tracking-tight">
                  {t("documents.uploadModal.fileLabel")}{" "}
                  <span className="text-red-500">*</span>
                </p>
                <div
                  className={`relative rounded-xl border-2 border-dashed transition-colors ${
                    file
                      ? "border-primary/40 bg-primary/5"
                      : "border-border bg-muted/20 hover:bg-muted/40"
                  } cursor-pointer`}
                  onClick={() => !loading && fileInputRef.current?.click()}
                >
                  <div className="flex flex-col items-center gap-2 py-6 px-4 text-center">
                    {file ? (
                      <>
                        <FileText className="h-8 w-8 text-primary" />
                        <p className="text-sm font-medium text-foreground truncate max-w-full px-4">
                          {file.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {(file.size / 1024 / 1024).toFixed(2)} MB -{" "}
                          {t("documents.uploadModal.changeFile")}
                        </p>
                      </>
                    ) : (
                      <>
                        <Upload className="h-8 w-8 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          {t("documents.uploadModal.filePlaceholder")}
                        </p>
                        <p className="text-xs text-muted-foreground/60">
                          {t("documents.uploadModal.maxSize")}
                        </p>
                      </>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={handleFileChange}
                    disabled={loading}
                  />
                </div>
              </div>

              {needsExpiry && (
                <div className="mt-3 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-400">
                  {t("documents.gdprNotice")}
                </div>
              )}
            </div>

            {/* Right panel — chain builder */}
            <div className="w-[360px] shrink-0 px-5 py-4 bg-muted/20 overflow-y-auto flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <GitBranch className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold">{t("documents.chain.panelTitle")}</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                {t("documents.chain.panelDesc")}
              </p>
              <div className="flex-1 min-h-0">
                <DocumentChainBuilder
                  steps={steps}
                  onChange={setSteps}
                  templates={templates}
                  onSaveTemplate={handleSaveTemplate}
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div className="px-6 pb-6 pt-4 border-t bg-muted/20 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={loading}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={loading || !isValid}
              className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600 border-0"
            >
              {loading ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  {t("documents.uploadModal.uploading")}
                </>
              ) : (
                <>
                  <Upload className="h-3.5 w-3.5 mr-1.5" />
                  {t("documents.uploadDocument")}
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
